import { supabase } from './client';
import type { Database } from '../../types/supabase';

export type OrganizationProfile = Database['public']['Tables']['organization_profiles']['Row'];
export type OrganizationInsert = Database['public']['Tables']['organization_profiles']['Insert'];

export async function createOrganizationProfile(data: Omit<OrganizationInsert, 'id' | 'created_at' | 'updated_at' | 'organization_id'> & { 
  domain?: string;
  email?: string;
  date_founded?: string;
}) {
  try {
    console.log('Creating organization profile with data:', data);
    
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if profile already exists
    const { data: existing, error: existingError } = await supabase
      .from('organization_profiles')
      .select('*')
      .eq('owner_id', user.id)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing profile:', existingError);
      throw new Error(`Failed to check existing profile: ${existingError.message}`);
    }

    // If profile exists, update it instead of creating a new one
    if (existing && existing.id) {
      console.log('Updating existing profile:', existing.id);
      return updateOrganizationProfile(existing.id, {
        name: data.name,
        phone: data.phone,
        address: data.address,
        type: data.type,
        domain: data.domain,
        email: data.email || null,
        date_founded: data.date_founded
      });
    }

    // First, get the entity_id if not provided
    let entity_id = data.entity_id;
    if (!entity_id) {
      const { data: entity, error: entityError } = await supabase
        .from('entities')
        .select('id')
        .eq('owner_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1);
      
      if (entityError) {
        console.error('Error getting entity:', entityError);
        throw new Error(`Failed to get entity: ${entityError.message}`);
      }
      
      if (entity && entity.length > 0) {
        entity_id = entity[0].id;
      } else {
        console.error('No entity found for owner_id:', user.id);
        throw new Error('No entity found for this user');
      }
    }

    // First create an organization record
    const { data: organization, error: orgError } = await supabase
      .from('organizations')
      .insert([{
        name: data.name,
        entity_id: entity_id,
        type: 'internal', // Use 'internal' type for company profiles
        domain: data.domain,
        address: data.address || null
      }])
      .select()
      .single();

    if (orgError) {
      console.error('Organization creation error:', orgError);
      throw new Error(`Failed to create organization: ${orgError.message}`);
    }

    // Create new profile with organization_id and entity_id
    const { data: profile, error } = await supabase
      .from('organization_profiles')
      .insert([{
        name: data.name,
        phone: data.phone,
        address: data.address,
        type: data.type,
        owner_id: user.id,
        entity_id: entity_id,
        organization_id: organization.id,
        domain: data.domain,
        email: data.email,
        date_founded: data.date_founded || null
      }])
      .select()
      .single();

    console.log('Insert result:', { profile, error });

    if (error) {
      console.error('Organization profile creation error:', error);
      if (error.code === '23505' || error.message.includes('duplicate')) {
        throw new Error('Organization profile already exists for this user');
      }
      throw error;
    }

    return profile;
  } catch (error) {
    console.error('Organization profile creation failed:', error);
    throw error;
  }
}

export async function getOrganizationProfile() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.log('No authenticated user found');
      return null;
    }

    console.log('Fetching profile for user:', user.id);

    // Get profile with organization data
    const { data: profile, error } = await supabase
      .from('organization_profiles')
      .select(`
        *,
        organization:organizations (
          id,
          name,
          domain,
          type,
          address,
          created_at,
          updated_at
        )
      `)
      .eq('owner_id', user.id)
      .maybeSingle();

    console.log('Profile fetch result:', { profile, error });

    if (error) {
      console.error('Error fetching organization profile:', error);
      return null;
    }

    return profile;
  } catch (error) {
    console.error('Failed to get organization profile:', error);
    return null; // Return null instead of throwing to prevent UI breakage
  }
}

export async function updateOrganizationProfile(
  id: string,
  updates: Partial<Omit<OrganizationProfile, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>
) {
  try {
    // First verify the profile exists and belongs to the user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Authentication required');
    }

    const { data: existing, error: existingError } = await supabase
      .from('organization_profiles')
      .select('id, owner_id, organization_id')
      .eq('id', id)
      .single();

    if (existingError) {
      throw new Error('Failed to verify profile ownership');
    }

    if (!existing || existing.owner_id !== user.id) {
      throw new Error('Profile not found or access denied');
    }
    
    // Format date_founded if it exists

    // If we have an organization_id, update the organization first
    if (existing.organization_id) {
      // Remove direct organization update - now handled by trigger
      console.log('Organization update will be handled by database trigger');
    }

    // Perform the update
    const { data: profile, error } = await supabase
      .from('organization_profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select(`
        *,
        organization:organizations (
          id,
          name,
          domain,
          type,
          address,
          created_at,
          updated_at
        )
      `)
      .single();

    if (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }

    return profile;
  } catch (error) {
    console.error('Update organization profile failed:', error);
    throw error;
  }
}

export async function updateOrganization(
  id: string,
  updates: Partial<Omit<OrganizationProfile, 'id' | 'owner_id' | 'created_at' | 'updated_at'>>
) {
  return updateOrganizationProfile(id, updates);
}